import React, { useEffect, useMemo, useState } from 'react';
import {
  Search,
  ArrowUpDown,
  Download,
  Loader2,
  FolderTree,
  Filter,
  Inbox,
} from 'lucide-react';
import type {
  ListReleasesQuery,
  ParticipantRole,
  ReleaseStatus,
  ReleaseSortBy,
  ReleaseSummary,
  SortDir,
} from '@shared/types';
import { adminApi } from './api';
import { useLang, type DictKey } from '../i18n';

interface HistoryProps {
  onOpen: (token: string) => void;
}

const ROLE_OPTIONS: { id: ParticipantRole | 'all'; tKey: DictKey }[] = [
  { id: 'all', tKey: 'common.all' },
  { id: 'model', tKey: 'role.model' },
  { id: 'makeup_artist', tKey: 'role.makeup_artist.short' },
  { id: 'stylist', tKey: 'role.stylist' },
  { id: 'retoucher', tKey: 'role.retoucher' },
  { id: 'assistant', tKey: 'role.assistant' },
  { id: 'other', tKey: 'role.other' },
];

const STATUS_OPTIONS: { id: ReleaseStatus; tKey: DictKey }[] = [
  { id: 'all', tKey: 'common.all' },
  { id: 'pending', tKey: 'common.pending' },
  { id: 'signed', tKey: 'common.signed' },
];

const SORT_OPTIONS: { id: ReleaseSortBy; tKey: DictKey }[] = [
  { id: 'created_at', tKey: 'history.sort.created_at' },
  { id: 'signed_at', tKey: 'history.sort.signed_at' },
  { id: 'shoot_title', tKey: 'history.sort.shoot_title' },
  { id: 'role', tKey: 'history.sort.role' },
];

const ROLE_DICT_KEY: Record<ParticipantRole, DictKey> = {
  model: 'role.model',
  makeup_artist: 'role.makeup_artist.short',
  stylist: 'role.stylist',
  retoucher: 'role.retoucher',
  assistant: 'role.assistant',
  other: 'role.other',
};

const ROLE_COLOR: Record<ParticipantRole, string> = {
  model: 'bg-purple-100 text-purple-700',
  makeup_artist: 'bg-pink-100 text-pink-700',
  stylist: 'bg-amber-100 text-amber-700',
  retoucher: 'bg-blue-100 text-blue-700',
  assistant: 'bg-green-100 text-green-700',
  other: 'bg-gray-100 text-gray-700',
};

const ROLE_INITIAL: Record<ParticipantRole, string> = {
  model: 'M',
  makeup_artist: 'V',
  stylist: 'С',
  retoucher: 'Р',
  assistant: 'А',
  other: '·',
};

const History: React.FC<HistoryProps> = ({ onOpen }) => {
  const { t, lang } = useLang();
  const [role, setRole] = useState<ParticipantRole | 'all'>('all');
  const [status, setStatus] = useState<ReleaseStatus>('all');
  const [sortBy, setSortBy] = useState<ReleaseSortBy>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [project, setProject] = useState('');
  const [grouped, setGrouped] = useState(false);

  const [releases, setReleases] = useState<ReleaseSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const query: ListReleasesQuery = useMemo(
    () => ({
      role: role === 'all' ? undefined : role,
      status,
      sortBy,
      sortDir,
      project: project.trim() || undefined,
    }),
    [role, status, sortBy, sortDir, project],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    adminApi
      .listReleases(query)
      .then((res) => {
        if (cancelled) return;
        setReleases(res.releases);
      })
      .catch((e) => !cancelled && setErr(e instanceof Error ? e.message : t('common.error')))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [query, t]);

  const groups = useMemo(() => {
    if (!grouped) return null;
    const map = new Map<string, ReleaseSummary[]>();
    for (const r of releases) {
      const key = r.shoot.title;
      const arr = map.get(key) ?? [];
      arr.push(r);
      map.set(key, arr);
    }
    return [...map.entries()];
  }, [grouped, releases]);

  const exportCsv = () => window.open(adminApi.csvUrl(query), '_blank');

  return (
    <div className="max-w-3xl mx-auto p-4 pb-20">
      <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-4 space-y-3 shadow-sm">
        <div className="flex gap-2 items-center">
          <Search size={16} className="text-gray-400 shrink-0" />
          <input
            type="text"
            value={project}
            onChange={(e) => setProject(e.target.value)}
            placeholder={t('history.search.placeholder')}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
          />
          <button
            onClick={exportCsv}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 transition"
            title="CSV"
          >
            <Download size={14} /> {t('history.exportCsv')}
          </button>
        </div>

        <div>
          <div className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">
            <Filter size={10} /> {t('common.role')}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {ROLE_OPTIONS.map((r) => (
              <button
                key={r.id}
                onClick={() => setRole(r.id)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition ${
                  role === r.id
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t(r.tKey)}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">
            {t('common.status')}
          </div>
          <div className="flex gap-1.5">
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setStatus(s.id)}
                className={`flex-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition ${
                  status === s.id
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t(s.tKey)}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
            <ArrowUpDown size={10} /> {t('common.sort')}
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as ReleaseSortBy)}
            className="text-sm border border-gray-300 rounded-lg px-2 py-1 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
          >
            {SORT_OPTIONS.map((s) => (
              <option key={s.id} value={s.id}>
                {t(s.tKey)}
              </option>
            ))}
          </select>
          <button
            onClick={() => setSortDir(sortDir === 'asc' ? 'desc' : 'asc')}
            className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded transition"
          >
            {sortDir === 'desc' ? '↓' : '↑'} {t(sortDir === 'desc' ? 'history.sortDirection.desc' : 'history.sortDirection.asc')}
          </button>
          <button
            onClick={() => setGrouped(!grouped)}
            className={`ml-auto text-xs px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 transition ${
              grouped ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            title={t('history.group')}
          >
            <FolderTree size={11} /> {t('history.group')}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center text-gray-400 py-16 text-sm">
          <Loader2 className="animate-spin mr-2" size={16} /> {t('history.loading')}
        </div>
      ) : err ? (
        <div className="text-red-600 text-sm p-4">{err}</div>
      ) : releases.length === 0 ? (
        <EmptyState message={t('history.empty')} />
      ) : groups ? (
        <div className="space-y-5">
          {groups.map(([title, items]) => (
            <div key={title}>
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 px-1">
                {title} <span className="text-gray-400 font-normal">· {items.length}</span>
              </h3>
              <div className="space-y-2">
                {items.map((r) => (
                  <ReleaseRow key={r.token} r={r} lang={lang} t={t} onOpen={onOpen} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {releases.map((r) => (
            <ReleaseRow key={r.token} r={r} lang={lang} t={t} onOpen={onOpen} />
          ))}
        </div>
      )}
    </div>
  );
};

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="bg-white border border-gray-200 border-dashed rounded-2xl p-10 text-center">
    <div className="inline-flex w-12 h-12 rounded-full bg-gray-100 text-gray-400 items-center justify-center mb-2">
      <Inbox size={20} />
    </div>
    <p className="text-sm text-gray-500">{message}</p>
  </div>
);

const formatDate = (iso: string, lang: 'ru' | 'en') => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(lang === 'ru' ? 'ru-RU' : 'en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const ReleaseRow: React.FC<{
  r: ReleaseSummary;
  lang: 'ru' | 'en';
  t: (k: DictKey) => string;
  onOpen: (token: string) => void;
}> = ({ r, lang, t, onOpen }) => {
  const signed = !!r.signedAt;
  return (
    <button
      onClick={() => onOpen(r.token)}
      className="w-full bg-white border border-gray-200 rounded-xl p-3 text-left hover:border-indigo-300 hover:shadow-sm transition flex gap-3 items-center"
    >
      <div
        className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold shrink-0 ${ROLE_COLOR[r.role]}`}
        aria-label={t(ROLE_DICT_KEY[r.role])}
      >
        {ROLE_INITIAL[r.role]}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="font-semibold text-sm text-gray-900 truncate">{r.shoot.title}</span>
          <span
            className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full flex items-center gap-1 shrink-0 ${
              signed ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${signed ? 'bg-green-500' : 'bg-amber-500'}`}
            />
            {signed ? t('status.signed') : t('status.pending')}
          </span>
        </div>
        <div className="text-xs text-gray-500 truncate">
          {t(ROLE_DICT_KEY[r.role])} · {r.signerName ?? '—'} · {formatDate(r.shoot.date, lang)}
        </div>
      </div>
    </button>
  );
};

export default History;
